import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ArrowLeft, RotateCcw, PlayCircle, ChevronDown, ChevronRight, Undo2, X, Plus } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { PageHeader } from '@/components/common/PageHeader'
import { CharCountTextarea } from '@/components/forms/CharCountTextarea'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { SessionMessage } from '@/components/sessions/SessionMessage'
import { SessionExpiryWarning } from '@/components/sessions/SessionExpiryWarning'
import { SessionContextSidebar } from '@/components/sessions/SessionContextSidebar'
import { OrchestrationChat } from '@/components/orchestration/OrchestrationChat'
import { OrchestrationResult } from '@/components/orchestration/OrchestrationResult'
import {
  ProjectSelector,
  ProjectArchivedBanner
} from '@/components/projects'
import { ConstraintsInput } from '@/components/forms/ConstraintsInput'
import { sessionKeys, useSession, useStartBackgroundTask } from '@/api/orchestrator'
import { projectKeys, useProject, useProjects, useEnhancePrompt } from '@/api/projects'
import { useSignalR } from '@/hooks/useSignalR'
import type { ConstraintSuggestion, OrchestrationEvent } from '@/lib/types'

interface FormErrors {
  task?: string
}

const MAX_TASK_LENGTH = 10000
const MIN_TASK_LENGTH = 10

/**
 * Connection state for orchestration
 */
type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'complete' | 'error'

/**
 * Orchestrate page for submitting tasks and viewing real-time SignalR progress.
 * Supports resuming existing sessions via URL parameter.
 *
 * Routes:
 * - /orchestrate - New task
 * - /orchestrate/:sessionId - Resume existing session
 */
export function OrchestratePage() {
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>()
  const [searchParams] = useSearchParams()
  const urlProjectId = searchParams.get('projectId')
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Form state
  const [task, setTask] = useState('')
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(
    urlProjectId ?? undefined
  )
  const [sessionConstraints, setSessionConstraints] = useState<string[]>([])
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submittedTask, setSubmittedTask] = useState<string>('')

  // Prompt enhancement state
  const [originalTask, setOriginalTask] = useState<string | null>(null)
  const [suggestedConstraints, setSuggestedConstraints] = useState<ConstraintSuggestion[]>([])
  const enhancePromptMutation = useEnhancePrompt()

  // Ref for scrolling to latest message when resuming
  const previousMessagesEndRef = useRef<HTMLDivElement>(null)

  // Fetch projects for selector
  const { data: projectsData, isLoading: projectsLoading } = useProjects({
    includeArchived: false
  })

  // Fetch selected project for constraints preview
  const { data: selectedProject } = useProject(selectedProjectId)

  // Fetch existing session if resuming
  const {
    data: existingSession,
    isLoading: isLoadingSession,
    error: sessionError,
  } = useSession(urlSessionId)

  // Scroll to latest message when resuming a session
  useEffect(() => {
    if (existingSession?.messages && existingSession.messages.length > 0 && previousMessagesEndRef.current) {
      // Small delay to ensure the messages have rendered
      setTimeout(() => {
        previousMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }, 100)
    }
  }, [existingSession?.messages])

  // Connection and events state for SignalR
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected')
  const [events, setEvents] = useState<OrchestrationEvent[]>([])
  const [newSessionId, setNewSessionId] = useState<string | null>(null)
  const [streamError, setStreamError] = useState<string | null>(null)

  // Start background task mutation
  const startTaskMutation = useStartBackgroundTask()

  // Track active session ID for SignalR subscription
  const activeSessionId = urlSessionId || newSessionId

  // SignalR event handlers
  const handleProgressUpdate = useCallback((payload: { message: string; timestamp: string }) => {
    setEvents(prev => [...prev, {
      eventType: 'progress_update',
      message: payload.message,
      timestamp: payload.timestamp,
    }])
  }, [])

  const handleToolCalled = useCallback((payload: { toolName: string; timestamp: string }) => {
    setEvents(prev => [...prev, {
      eventType: 'tool_called',
      message: `Calling tool: ${payload.toolName}`,
      timestamp: payload.timestamp,
    }])
  }, [])

  const handleSessionComplete = useCallback((payload: { response: string; timestamp: string }) => {
    setConnectionState('complete')
    setEvents(prev => [...prev, {
      eventType: 'complete',
      message: payload.response || 'Task completed',
      timestamp: payload.timestamp,
      data: { response: payload.response, sessionId: activeSessionId }
    }])
    setIsSubmitting(false)
    setTask('')
    
    // Invalidate queries
    queryClient.invalidateQueries({ queryKey: projectKeys.all })
    queryClient.invalidateQueries({ queryKey: sessionKeys.all })
    
    // Navigate to session detail
    if (activeSessionId) {
      toast.success('Task completed', {
        description: 'Your task has finished. View the results.',
      })
      navigate(`/sessions/${activeSessionId}`)
    }
  }, [activeSessionId, queryClient, navigate])

  const handleSessionError = useCallback((payload: { error: string; timestamp: string }) => {
    setConnectionState('error')
    setStreamError(payload.error)
    setEvents(prev => [...prev, {
      eventType: 'error',
      message: payload.error,
      timestamp: payload.timestamp,
    }])
    setIsSubmitting(false)
  }, [])

  const handleSessionStatusChanged = useCallback((payload: { status: string; timestamp: string }) => {
    // Update connection state based on session status
    if (payload.status === 'Running') {
      setConnectionState('connected')
    } else if (payload.status === 'Completed') {
      setConnectionState('complete')
    } else if (payload.status === 'Failed' || payload.status === 'Cancelled') {
      setConnectionState('error')
    }
    
    setEvents(prev => [...prev, {
      eventType: 'progress_update',
      message: `Session status: ${payload.status}`,
      timestamp: payload.timestamp,
    }])
  }, [])

  // SignalR hook for real-time updates
  useSignalR({
    sessionId: activeSessionId || undefined,
    enabled: isSubmitting && !!activeSessionId,
    onProgressUpdate: handleProgressUpdate,
    onToolCalled: handleToolCalled,
    onSessionComplete: handleSessionComplete,
    onSessionError: handleSessionError,
    onSessionStatusChanged: handleSessionStatusChanged,
    onConnectionStateChange: (state) => {
      if (state === 'connected' && connectionState === 'connecting') {
        setConnectionState('connected')
      }
    },
  })

  // Track current session ID (from URL or newly created)
  const currentSessionId = urlSessionId || newSessionId

  // Get session metadata for expiry warning
  const sessionMetadata = existingSession?.metadata

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!task.trim()) {
      newErrors.task = 'Task is required'
    } else if (task.trim().length < MIN_TASK_LENGTH) {
      newErrors.task = `Task must be at least ${MIN_TASK_LENGTH} characters`
    } else if (task.length > MAX_TASK_LENGTH) {
      newErrors.task = `Task must be less than ${MAX_TASK_LENGTH} characters`
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setSubmittedTask(task.trim())
    setIsSubmitting(true)
    setConnectionState('connecting')
    setEvents([])
    setStreamError(null)

    try {
      const result = await startTaskMutation.mutateAsync({
        userRequest: task.trim(),
        sessionId: urlSessionId || undefined,
        projectId: selectedProjectId,
        constraints: sessionConstraints.length > 0 ? sessionConstraints : undefined,
      })

      setNewSessionId(result.sessionId)
      
      // Add initial event
      setEvents([{
        eventType: 'agent_started',
        message: result.message || 'Task started',
        timestamp: new Date().toISOString(),
        data: { sessionId: result.sessionId }
      }])

      toast.success('Task started', {
        description: 'Your task is running. You can safely navigate away.',
      })

      // Navigate to session detail page
      navigate(`/sessions/${result.sessionId}`)
    } catch (error) {
      setConnectionState('error')
      setStreamError(error instanceof Error ? error.message : 'Failed to start task')
      setIsSubmitting(false)
    }
  }

  const handleReset = () => {
    setSubmittedTask('')
    setIsSubmitting(false)
    setConnectionState('disconnected')
    setEvents([])
    setNewSessionId(null)
    setStreamError(null)
    setTask('')
    setSessionConstraints([])
    setOriginalTask(null)
    setSuggestedConstraints([])
    setErrors({})
  }

  // Handle prompt enhancement
  const handleEnhancePrompt = () => {
    if (!task.trim()) return

    enhancePromptMutation.mutate(
      {
        prompt: task.trim(),
        projectId: selectedProjectId,
        constraints: sessionConstraints
      },
      {
        onSuccess: (data) => {
          // Store original task for undo
          setOriginalTask(task)
          // Update task with enhanced version
          setTask(data.enhancedPrompt)
          // Store suggested constraints
          setSuggestedConstraints(data.suggestedConstraints)

          if (data.error) {
            toast.warning('Enhancement partial', {
              description: data.error
            })
          } else {
            toast.success('Prompt enhanced', {
              description: 'Your prompt has been improved. Click undo to revert.'
            })
          }
        },
        onError: (error) => {
          toast.error('Enhancement failed', {
            description: error instanceof Error ? error.message : 'Please try again'
          })
        }
      }
    )
  }

  // Handle undo enhancement
  const handleUndoEnhance = () => {
    if (originalTask !== null) {
      setTask(originalTask)
      setOriginalTask(null)
      setSuggestedConstraints([])
      toast.info('Reverted to original prompt')
    }
  }

  // Handle adding a suggested constraint
  const handleAddSuggestedConstraint = (constraint: string) => {
    if (!sessionConstraints.includes(constraint)) {
      setSessionConstraints([...sessionConstraints, constraint])
      // Remove from suggestions
      setSuggestedConstraints(prev => prev.filter(s => s.constraint !== constraint))
      toast.success('Constraint added')
    }
  }

  // Handle dismissing a suggested constraint
  const handleDismissSuggestion = (constraint: string) => {
    setSuggestedConstraints(prev => prev.filter(s => s.constraint !== constraint))
  }

  // Determine if currently streaming
  const isStreaming =
    connectionState === 'connecting' || connectionState === 'connected'
  const isComplete = connectionState === 'complete'
  const hasError = connectionState === 'error' || streamError !== null

  // Extract result data from complete event
  const completeEvent = events.find((e) => e.eventType === 'complete')
  const errorEvent = events.find((e) => e.eventType === 'error')
  const resultRunId = completeEvent?.data && typeof completeEvent.data === 'object' && completeEvent.data !== null && 'runId' in completeEvent.data ? String(completeEvent.data.runId) : undefined
  const resultPrUrl = completeEvent?.data && typeof completeEvent.data === 'object' && completeEvent.data !== null && 'prUrl' in completeEvent.data ? String(completeEvent.data.prUrl) : undefined

  // Show sidebar when we have session metadata (resuming or after streaming starts)
  // Track whether constraints section is expanded
  const [constraintsExpanded, setConstraintsExpanded] = useState(false)
  
  // Calculate total constraints count for the collapsed summary
  const projectConstraintsCount = selectedProject?.constraints?.length ?? 0
  const sessionConstraintsCount = sessionConstraints.length
  const totalConstraintsCount = projectConstraintsCount + sessionConstraintsCount

  const showSidebar = sessionMetadata || (currentSessionId && events.length > 0)

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        {/* Back Link */}
        <Button variant="ghost" asChild>
          <Link to="/sessions">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Sessions
          </Link>
        </Button>

        {/* Header with Project Selector (for new tasks) */}
        {!urlSessionId ? (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Orchestrate</h1>
              <p className="text-muted-foreground">Submit a task and watch the AI agent work</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Project:</span>
              <div className="w-64">
                <ProjectSelector
                  projects={projectsData?.projects ?? []}
                  value={selectedProjectId}
                  onChange={setSelectedProjectId}
                  isLoading={projectsLoading}
                  placeholder="Select project..."
                  clearable
                />
              </div>
            </div>
          </div>
        ) : (
          <PageHeader
            title="Orchestrate"
            description="Continue your conversation"
          />
        )}

        {/* Archived project warning */}
        {selectedProject?.isArchived && (
          <ProjectArchivedBanner
            variant="warning"
            dismissible
          />
        )}

        {/* Two-column layout: Main content + Context Sidebar */}
        <div className="flex gap-6">
          {/* Main conversation area - wider for task input */}
          <div className="flex-1 min-w-0 space-y-6">

        {/* Session Expiry Warning */}
        {sessionMetadata && (
          <SessionExpiryWarning
            expiresAt={sessionMetadata.expiresAt}
            variant="banner"
          />
        )}

        {/* Session Fetch Error */}
        {sessionError && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Unable to load session. It may have expired or been deleted.
            </AlertDescription>
          </Alert>
        )}

        {/* Loading Session */}
        {isLoadingSession && urlSessionId && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
                Loading session...
              </div>
            </CardContent>
          </Card>
        )}

        {/* Previous Messages (when resuming) */}
        {existingSession?.messages && existingSession.messages.length > 0 && (
          <Card>
            <CardContent className="p-4 space-y-4">
              <h3 className="font-medium">Previous Messages</h3>
              {existingSession.messages.map((msg, index) => (
                <SessionMessage key={index} message={msg} />
              ))}
              {/* Scroll anchor for auto-scroll to latest message */}
              <div ref={previousMessagesEndRef} />
            </CardContent>
          </Card>
        )}

        {/* Main Task Form Card - NEW TASK */}
        {!urlSessionId && (
          <Card>
            <CardContent className="p-6 space-y-6">
              {/* Task Input - Primary Focus */}
              <div className="space-y-2">
                <label htmlFor="task" className="text-lg font-medium">
                  What would you like to do?
                </label>
                <CharCountTextarea
                  id="task"
                  aria-label="What would you like to do?"
                  value={task}
                  onChange={(e) => {
                    setTask(e.target.value)
                    if (errors.task) setErrors({})
                    // Clear undo state when user edits the text
                    if (originalTask !== null && e.target.value !== task) {
                      setOriginalTask(null)
                    }
                  }}
                  placeholder="Describe your task in detail... (e.g., 'Implement user authentication with OAuth2 and JWT tokens. Add login/logout endpoints and protect the /api/users routes.')"
                  maxLength={MAX_TASK_LENGTH}
                  minLength={MIN_TASK_LENGTH}
                  rows={6}
                  disabled={isStreaming}
                  className={errors.task ? 'border-destructive' : ''}
                  onEnhance={handleEnhancePrompt}
                  isEnhancing={enhancePromptMutation.isPending}
                  disableEnhance={isStreaming}
                />
                {errors.task && (
                  <p className="mt-1 text-sm text-destructive">{errors.task}</p>
                )}

                {/* Undo Enhancement Banner */}
                {originalTask !== null && (
                  <Alert className="bg-muted">
                    <Undo2 className="h-4 w-4" />
                    <AlertDescription className="flex items-center justify-between">
                      <span>Prompt enhanced. Original saved.</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleUndoEnhance}
                        className="ml-2"
                      >
                        <Undo2 className="h-3 w-3 mr-1" />
                        Undo
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Suggested Constraints */}
                {suggestedConstraints.length > 0 && (
                  <div className="space-y-2 pt-2">
                    <p className="text-sm font-medium text-muted-foreground">
                      Suggested constraints:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {suggestedConstraints.map((suggestion, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="flex items-center gap-1 pr-1 cursor-default group"
                          title={suggestion.reason}
                        >
                          <span className="truncate max-w-[200px]">
                            {suggestion.constraint.split(':').slice(-1)[0]}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 p-0 hover:bg-green-100 dark:hover:bg-green-900"
                            onClick={() => handleAddSuggestedConstraint(suggestion.constraint)}
                            title="Add this constraint"
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 p-0 hover:bg-red-100 dark:hover:bg-red-900"
                            onClick={() => handleDismissSuggestion(suggestion.constraint)}
                            title="Dismiss"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Collapsible Constraints Section */}
              <Collapsible open={constraintsExpanded} onOpenChange={setConstraintsExpanded}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-0 hover:bg-transparent">
                    {constraintsExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span className="text-sm font-medium">Constraints</span>
                    {totalConstraintsCount > 0 && (
                      <span className="text-xs text-muted-foreground">
                        ({projectConstraintsCount > 0 ? `${projectConstraintsCount} inherited` : ''}
                        {projectConstraintsCount > 0 && sessionConstraintsCount > 0 ? ' + ' : ''}
                        {sessionConstraintsCount > 0 ? `${sessionConstraintsCount} custom` : ''})
                      </span>
                    )}
                    {totalConstraintsCount === 0 && (
                      <span className="text-xs text-muted-foreground">(optional)</span>
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-4 space-y-4">
                  {/* Project constraints (read-only) */}
                  {selectedProject && selectedProject.constraints && selectedProject.constraints.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">
                        From project "{selectedProject.name}"
                      </p>
                      <ConstraintsInput
                        value={selectedProject.constraints}
                        onChange={() => {}}
                        disabled
                      />
                    </div>
                  )}

                  {/* Session constraints (editable) */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-muted-foreground">
                      Session constraints
                    </p>
                    <ConstraintsInput
                      value={sessionConstraints}
                      onChange={setSessionConstraints}
                      maxConstraints={10}
                      disabled={isStreaming}
                    />
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Submit Button */}
              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isStreaming || !task.trim()}
                  size="lg"
                  className="flex-1"
                >
                  {isStreaming ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      {connectionState === 'connecting'
                        ? 'Starting...'
                        : 'Processing...'}
                    </>
                  ) : (
                    <>
                      <PlayCircle className="h-4 w-4 mr-2" />
                      Start Task
                    </>
                  )}
                </Button>

                {(isComplete || hasError) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={handleReset}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    New Task
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Task Form - RESUME SESSION */}
        {urlSessionId && (
          <Card>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="task" className="text-lg font-medium">
                    Continue Conversation
                  </label>
                  <CharCountTextarea
                    id="task"
                    aria-label="What would you like to do?"
                    value={task}
                    onChange={(e) => {
                      setTask(e.target.value)
                      if (errors.task) setErrors({})
                    }}
                    placeholder="What would you like to do next?"
                    maxLength={MAX_TASK_LENGTH}
                    minLength={MIN_TASK_LENGTH}
                    rows={4}
                    disabled={isStreaming}
                    className={errors.task ? 'border-destructive' : ''}
                  />
                  {errors.task && (
                    <p className="mt-1 text-sm text-destructive">{errors.task}</p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={isStreaming || !task.trim()}
                    size="lg"
                    className="flex-1"
                  >
                    {isStreaming ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                        {connectionState === 'connecting'
                          ? 'Starting...'
                          : 'Processing...'}
                      </>
                    ) : (
                      <>
                        <PlayCircle className="h-4 w-4 mr-2" />
                        Resume Task
                      </>
                    )}
                  </Button>

                  {(isComplete || hasError) && (
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      onClick={handleReset}
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      New Task
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Connecting Indicator - shows submitted task */}
        {connectionState === 'connecting' && events.length === 0 && (
          <Card>
            <CardContent className="p-6 space-y-4">
              {submittedTask && (
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">Your request:</p>
                  <p className="text-sm whitespace-pre-wrap">{submittedTask}</p>
                </div>
              )}
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
                Connecting to orchestrator...
              </div>
            </CardContent>
          </Card>
        )}

        {/* Events Stream - shows submitted task at top */}
        {events.length > 0 && !isComplete && !hasError && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                Progress
                {connectionState === 'connecting' && events.length > 0 && (
                  <span className="text-sm font-normal text-yellow-600">
                    (Reconnecting...)
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Show submitted task for context */}
              {submittedTask && (
                <div className="bg-muted/50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-muted-foreground mb-1">Your request:</p>
                  <p className="text-sm whitespace-pre-wrap">{submittedTask}</p>
                </div>
              )}
              <OrchestrationChat
                events={events}
                isConnected={connectionState === 'connected'}
                isConnecting={connectionState === 'connecting'}
                showTimestamps
              />
            </CardContent>
          </Card>
        )}

        {/* Result Display */}
        {isComplete && completeEvent && (
          <OrchestrationResult
            success={true}
            result={completeEvent.message}
            runId={resultRunId}
            prUrl={resultPrUrl}
            sessionId={currentSessionId || undefined}
          />
        )}

        {/* Error Display */}
        {hasError && (
          <OrchestrationResult
            success={false}
            error={
              streamError ||
              errorEvent?.message ||
              'An unexpected error occurred'
            }
            sessionId={currentSessionId || undefined}
          />
        )}
          </div>

          {/* Context Sidebar - shows when viewing/resuming session */}
          {showSidebar && sessionMetadata && (
            <div className="hidden lg:block w-72 shrink-0">
              <SessionContextSidebar
                session={sessionMetadata}
                className="sticky top-4"
              />
            </div>
          )}
        </div>
      </div>
    </PageShell>
  )
}
